import { createHmac, timingSafeEqual } from 'node:crypto';

import { GpmPayWebhookSignatureError } from '../core/errors.js';
import type { WebhookPayload } from '../types/webhook.js';

/** Header carrying the signature. Overridable per webhook setting. */
export const SIGNATURE_HEADER = 'X-GPMPay-Signature';

/** Header carrying the event name (WordPress driver). */
export const EVENT_HEADER = 'X-GPMPay-Event';

/** Clock-skew window the backend also enforces. */
export const DEFAULT_TOLERANCE_SECONDS = 300;

export interface VerifyWebhookInput {
  /**
   * The **exact bytes** of the request body.
   *
   * Never `JSON.stringify(req.body)` — re-serializing a parsed body changes
   * whitespace and key order, and the signature will never match.
   */
  rawBody: string | Buffer | Uint8Array;
  /** Value of `X-GPMPay-Signature`: `t=<unix_seconds>,v1=<hex_sha256>`. */
  signature: string;
  /**
   * `authorizationSecret` for an HTTP+HMAC webhook, or `wpSecret` for the
   * WordPress driver.
   */
  secret: string;
  /** Skew window in seconds. Default 300. Pass 0 to disable (tests only). */
  toleranceSeconds?: number;
  /** @internal Test seam. */
  now?: () => number;
}

function toBuffer(input: string | Buffer | Uint8Array): Buffer {
  if (Buffer.isBuffer(input)) return input;
  if (typeof input === 'string') return Buffer.from(input, 'utf8');
  return Buffer.from(input);
}

/**
 * Parse `t=…,v1=…`, splitting each pair on its **first** `=` only — hex and
 * base64 values may legitimately contain `=`.
 */
function parseSignatureHeader(signature: string): Record<string, string> {
  const parts: Record<string, string> = {};
  for (const segment of signature.split(',')) {
    const index = segment.indexOf('=');
    if (index > 0) {
      parts[segment.slice(0, index).trim()] = segment.slice(index + 1).trim();
    }
  }
  return parts;
}

/**
 * Verify a webhook signature, throwing a typed error explaining exactly why it
 * failed.
 *
 * Matches `apps/backend/src/modules/webhook-delivery/hmac.util.ts`: the signed
 * string is `` `${t}.${rawBody}` ``, hashed with HMAC-SHA256 and compared in
 * constant time.
 *
 * @throws {GpmPayWebhookSignatureError}
 */
export function assertWebhookSignature(input: VerifyWebhookInput): {
  timestamp: number;
} {
  const { signature, secret, now = Date.now } = input;
  const tolerance = input.toleranceSeconds ?? DEFAULT_TOLERANCE_SECONDS;

  if (typeof secret !== 'string' || secret === '') {
    throw new GpmPayWebhookSignatureError(
      'Webhook secret is empty. Pass the secret you configured on the webhook ' +
        'setting (e.g. process.env.GPMPAY_WEBHOOK_SECRET).',
      'missing_secret',
    );
  }

  if (typeof signature !== 'string' || signature.trim() === '') {
    throw new GpmPayWebhookSignatureError(
      `Missing ${SIGNATURE_HEADER} header.`,
      'malformed_header',
    );
  }

  const parts = parseSignatureHeader(signature);
  const timestamp = Number(parts.t);
  const v1 = parts.v1;

  if (!Number.isFinite(timestamp) || timestamp <= 0 || !v1) {
    throw new GpmPayWebhookSignatureError(
      `Malformed ${SIGNATURE_HEADER}: "${signature}". Expected "t=<unix_seconds>,v1=<hex>".`,
      'malformed_header',
    );
  }

  if (tolerance > 0 && Math.abs(now() / 1000 - timestamp) > tolerance) {
    throw new GpmPayWebhookSignatureError(
      `Webhook timestamp is outside the ${String(tolerance)}s tolerance (t=${String(timestamp)}). ` +
        'Check for clock skew between your server and GPM Pay.',
      'timestamp_skew',
    );
  }

  // Hash over bytes, not characters: Vietnamese transfer contents are
  // multi-byte, and hashing the string length would silently diverge.
  const raw = toBuffer(input.rawBody);
  const expected = createHmac('sha256', secret)
    .update(Buffer.concat([Buffer.from(`${String(timestamp)}.`, 'utf8'), raw]))
    .digest();

  const provided = Buffer.from(v1, 'hex');
  if (
    expected.length !== provided.length ||
    !timingSafeEqual(expected, provided)
  ) {
    throw new GpmPayWebhookSignatureError(
      'Webhook signature mismatch — wrong secret, or the body was modified in transit. ' +
        'Make sure you are verifying the raw request body, not a re-serialized object.',
      'mismatch',
    );
  }

  return { timestamp };
}

/** Boolean form of {@link assertWebhookSignature}. */
export function verifyWebhookSignature(input: VerifyWebhookInput): boolean {
  try {
    assertWebhookSignature(input);
    return true;
  } catch {
    return false;
  }
}

/**
 * Produce a signature header. Useful for testing your own handler and for
 * generating fixtures; GPM Pay signs real deliveries itself.
 */
export function signWebhookPayload(params: {
  rawBody: string | Buffer | Uint8Array;
  secret: string;
  /** Unix seconds. Defaults to now. */
  timestamp?: number;
}): string {
  const timestamp = params.timestamp ?? Math.floor(Date.now() / 1000);
  const raw = toBuffer(params.rawBody);
  const digest = createHmac('sha256', params.secret)
    .update(Buffer.concat([Buffer.from(`${String(timestamp)}.`, 'utf8'), raw]))
    .digest('hex');
  return `t=${String(timestamp)},v1=${digest}`;
}

export interface GpmPayWebhookEvent {
  /** From `X-GPMPay-Event`; defaults to `transaction.created`. */
  type: string;
  /** Unix seconds the delivery was signed at. */
  timestamp: number;
  payload: WebhookPayload;
  /** The raw body, for logging or re-verification. */
  rawBody: string;
}

function headerValue(
  headers: Record<string, string | string[] | undefined> | undefined,
  name: string,
): string | undefined {
  if (!headers) return undefined;
  const target = name.toLowerCase();
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() !== target) continue;
    return Array.isArray(value) ? value[0] : value;
  }
  return undefined;
}

/**
 * Verify a delivery and parse it into a typed event.
 *
 * @throws {GpmPayWebhookSignatureError} when verification fails.
 */
export function constructWebhookEvent(
  input: VerifyWebhookInput & {
    headers?: Record<string, string | string[] | undefined>;
  },
): GpmPayWebhookEvent {
  const { timestamp } = assertWebhookSignature(input);
  const rawBody = toBuffer(input.rawBody).toString('utf8');

  return {
    type: headerValue(input.headers, EVENT_HEADER) ?? 'transaction.created',
    timestamp,
    payload: JSON.parse(rawBody) as WebhookPayload,
    rawBody,
  };
}

/**
 * Constant-time comparison for webhooks configured with
 * `authorizationType: 'API_KEY'`, where the raw secret is sent in a header
 * instead of being used to sign the body.
 */
export function verifyApiKeyHeader(
  received: string | undefined,
  expected: string,
): boolean {
  if (typeof received !== 'string' || expected === '') return false;
  const a = Buffer.from(received, 'utf8');
  const b = Buffer.from(expected, 'utf8');
  if (a.length !== b.length) return false;
  return timingSafeEqual(a, b);
}
